import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapPin, CalendarDays, BookOpen, Sparkles, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const TIDS = {
  page: "shared-chapter-page",
  story: (id) => `shared-chapter-story-${id}`,
  joinCta: "shared-chapter-join-cta",
  notFound: "shared-chapter-not-found",
};

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Public read-only chapter page — no auth wrapper. Anyone with a /share/chapter/:slug
 * URL can read along. Doubles as a viral hook: the bottom CTA lets visitors
 * start their own TravelBio via the author's referral code (so the author
 * earns +250 pts on every conversion).
 */
export default function SharedChapterPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/share/chapter/data/${slug}`);
        setData(data);
      } catch (e) {
        setError(e.response?.status === 404 ? "not_found" : "error");
      }
    })();
  }, [slug]);

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
        <Sparkles className="w-12 h-12 text-tb-navy/40 mb-3" />
        <h1 data-testid={TIDS.notFound} className="font-serif-it text-tb-navy text-3xl">
          this chapter has flown away
        </h1>
        <p className="font-hand text-tb-navy/60 mt-2 max-w-sm">
          The share link may have been revoked or never existed.
        </p>
        <button
          onClick={() => nav("/")}
          className="mt-5 px-4 py-2 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand"
        >
          back to travelbio
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="font-hand text-tb-navy/55 italic">~ loading chapter ~</p>
      </div>
    );
  }

  const { year, author, stories } = data;
  const referralUrl = author.referral_code ? `/r/${author.referral_code}` : "/auth";

  return (
    <div data-testid={TIDS.page} className="min-h-screen bg-white">
      {/* Header banner */}
      <header className="bg-tb-navy text-tb-gold-bright px-5 py-8 md:py-12 border-b-4 border-tb-gold">
        <div className="max-w-3xl mx-auto">
          <p className="font-hand text-sm uppercase tracking-widest opacity-70 flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> a travelbio chapter
          </p>
          <h1 className="font-serif-it text-5xl md:text-7xl mt-2 leading-none">
            Chapter {year}
          </h1>
          <p className="font-hand text-lg md:text-xl mt-3 text-tb-gold/95">
            shared by <span className="font-bold">{author.name}</span> · {stories.length} {stories.length === 1 ? "story" : "stories"}
          </p>
        </div>
      </header>

      {/* Stories */}
      <main className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        {stories.length === 0 ? (
          <div className="text-center py-12">
            <Sparkles className="w-10 h-10 mx-auto text-tb-navy/30 mb-2" />
            <p className="font-serif-it text-tb-navy text-2xl">no stories yet</p>
            <p className="font-hand text-tb-navy/60 mt-1">
              {author.name} hasn't logged any stories in {year} yet — check back soon.
            </p>
          </div>
        ) : stories.map((s) => (
          <article
            key={s.id}
            data-testid={TIDS.story(s.id)}
            className="rounded-2xl bg-white border-2 border-tb-cream-3 overflow-hidden shadow-sm"
          >
            {s.image && (
              <img src={s.image} alt={s.trip_name} className="w-full h-56 md:h-72 object-cover" />
            )}
            <div className="p-5 md:p-6">
              <h2 className="font-serif-it text-tb-navy text-3xl md:text-4xl">
                {s.trip_name}
              </h2>
              <p className="font-hand text-tb-navy/65 text-sm flex items-center gap-3 mt-1 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {s.country || "—"}
                </span>
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {fmt(s.start_date)}{s.end_date && ` → ${fmt(s.end_date)}`}
                </span>
              </p>
              {(s.favorite_moments || []).length > 0 && (
                <div className="mt-4">
                  <p className="font-hand text-tb-coral text-xs uppercase tracking-widest mb-2">
                    favorite moments
                  </p>
                  <ul className="space-y-1.5">
                    {s.favorite_moments.map((m, i) => (
                      <li key={`${s.id}-m-${i}`} className="flex items-start gap-3 font-ui text-tb-navy/90 leading-relaxed">
                        <span className="font-serif-it text-tb-coral text-xl leading-none mt-0.5 shrink-0">
                          {i + 1}.
                        </span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </article>
        ))}
      </main>

      {/* CTA — referral funnel */}
      <section className="bg-tb-navy text-tb-gold-bright border-t-4 border-tb-gold mt-8">
        <div className="max-w-3xl mx-auto px-5 py-10 md:py-12 text-center">
          <p className="font-script text-4xl md:text-5xl leading-none">
            start your own travel journal
          </p>
          <p className="font-hand text-base md:text-lg mt-3 text-tb-gold/90 max-w-xl mx-auto">
            TravelBio is where {author.name} keeps every trip — past, planned, and dreamed.
            Dream it. Plan it. Book it. Cherish it.
          </p>
          <a
            data-testid={TIDS.joinCta}
            href={referralUrl}
            className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-tb-gold text-tb-navy font-hand text-lg shadow-lg hover:scale-[1.02] transition tb-tap"
          >
            join travelbio <ExternalLink className="w-4 h-4" />
          </a>
          {author.referral_code && (
            <p className="font-hand text-xs italic mt-2 text-tb-gold/70">
              ✦ {author.name} earns +250 pts when you join
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
